import { rootApi } from '../rootApi';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import { List, ListItem, CreateListDto, UpdateListDto, ListEntityType, RemoveListItemsDto, UpdateListItemsDto, UpdateListOwnerDto, UpdateListAccessDto } from './lists.types';

export const listsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getListById: builder.query<List, string>({
      query: (list_id) => ({
        url: `/lists/${list_id}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Lists', id: arg }],
    }),
    getListsPaginated: builder.query<PaginatedResponse<List>, PageOptions & { search_query?: string, entity_type: ListEntityType }>({
      query: (data) => ({
        url: `/lists?page=${data.page}&per_page=${data.per_page}`,
        params: {
          search_query: data.search_query,
          entity_type: data.entity_type,
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'EntityLists', id: arg.entity_type }],
    }),
    getListItemsPaginated: builder.query<PaginatedResponse<ListItem>, PageOptions & { list_id: string }>({
      query: (data) => ({
        url: `/lists/${data.list_id}/items?page=${data.page}&per_page=${data.per_page}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ListItems', id: arg.list_id }],
    }),

    createList: builder.mutation<APIResponse, CreateListDto>({
      query: (data) => ({
        url: `/lists`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'EntityLists', id: arg.entity_type }],
    }),

    updateList: builder.mutation<APIResponse, { list_id: string; data: UpdateListDto }>({
      query: ({ list_id, data }) => ({
        url: `/lists/${list_id}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Lists', id: arg.list_id }, { type: 'EntityLists' }],
    }),

    addListItems: builder.mutation<APIResponse, { list_id: string; data: UpdateListItemsDto }>({
      query: ({ list_id, data }) => ({
        url: `/lists/${list_id}/items/add`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'ListItems', id: arg.list_id }],
    }),
    removeListItems: builder.mutation<APIResponse, { list_id: string; data: RemoveListItemsDto }>({
      query: ({ list_id, data }) => ({
        url: `/lists/${list_id}/items/remove`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'ListItems', id: arg.list_id }],
    }),

    updateListAccess: builder.mutation<APIResponse, { list_id: string; data: UpdateListAccessDto }>({
      query: ({ list_id, data }) => ({
        url: `/lists/${list_id}/access`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Lists', id: arg.list_id }],
    }),

    changeListOwner: builder.mutation<APIResponse, { list_id: string; data: UpdateListOwnerDto }>({
      query: ({ list_id, data }) => ({
        url: `/lists/${list_id}/owner`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Lists', id: arg.list_id }],
    }),

    getPublicListsByEntityType: builder.query<PaginatedResponse<List>, { organizationId: string, entityType: ListEntityType, searchQuery?: string } & PageOptions>({
      query: ({ organizationId, entityType, searchQuery, page = 1, per_page = 10 }) => ({
        url: `/lists/public-lists`,
        params: {
          organization_id: organizationId,
          entity_type: entityType,
          search_query: searchQuery,
          page,
          per_page,
        },
      })
    }),

  }),
});

export const {
  useGetListByIdQuery,
  useGetListsPaginatedQuery,
  useGetListItemsPaginatedQuery,
  useCreateListMutation,
  useUpdateListMutation,
  useAddListItemsMutation,
  useRemoveListItemsMutation,
  useUpdateListAccessMutation,
  useChangeListOwnerMutation,
  useGetPublicListsByEntityTypeQuery,
} = listsApi;
